"use client";

import { c as _c } from "react/compiler-runtime";
import { Toaster as Sonner, ToasterProps } from "sonner";
import { RiCheckboxCircleFill, RiCloseCircleFill, RiErrorWarningFill, RiInformationFill, RiLoader2Fill } from "react-icons/ri";
import switzer from "@/app/font";
import { useIsMobile } from "@/contexts/is-mobile-provider";
import { cx } from "@/lib/tailwind-utils";
import { useThemeStore } from "@/stores/theme-store";

// sonner ships its own surface: its own grey, its own corner radius, and a drop
// shadow under every toast. The previous overrides were set on the Toaster
// wrapper, which never reaches the toast element, so the toasts were the one
// surface in the console not drawn from the design tokens — visibly a different
// card than the panels they float over. These put them back on the platform:
// one flat card, a hairline border, and no drop shadow doing work the border
// already does.
//
// Most of sonner's own rules are wrapped in `:where()` and carry no
// specificity, so a plain class wins. Its two button rules are not, hence the
// `!` prefixes there.
function toastClassNames(hasCloseButton: boolean) {
  return {
    toast: cx("items-start gap-3 rounded-xl border border-border bg-background p-4 shadow-none",
    // The close button is absolutely positioned in the top-right corner;
    // without this the title runs underneath it.
    hasCloseButton && "pr-11",
    // Title-only toast with an action: the right of the card becomes a
    // column instead: close on top, action beneath it, both on the same edge.
    // The action is a flex item, so it sits inside that reserved gutter
    // rather than beside it — give the padding back, and hold enough height
    // for the two to stack without the action riding up under the close
    // button on a short toast.
    "[&:has([data-button]):not(:has([data-description]))]:min-h-[5rem]", "[&:has([data-button]):not(:has([data-description]))]:pr-4",
    // With a description the action drops to its own row under the text.
    // Beside it, the action takes the card's width away from the text and a
    // two-line message wraps into a tall, thin column of single words. The
    // content fills the first row (see `content`), so the buttons wrap; the
    // first one is indented past the icon (1.75rem) less its own px-2.5, so
    // its label lines up with the text above it.
    "has-[[data-description]]:flex-wrap has-[[data-description]]:gap-y-2", "[&>[data-content]:has([data-description])+[data-button]]:!ml-[1.125rem]"),
    // sonner centres the icon against the whole card, so it drifts down beside
    // the second line as soon as a description wraps. Pin it to the first line.
    icon: "mt-0.5 shrink-0 self-start",
    // Takes the row so the action stays on the right edge and a long title
    // wraps against it instead of pushing it off the card.
    // With a description it claims the whole first row bar the icon (size-4 +
    // gap-3 = 1.75rem), which is what pushes the action onto the next row.
    content: "min-w-0 flex-1 gap-0.5 has-[[data-description]]:basis-[calc(100%-1.75rem)]",
    title: "text-sm font-medium leading-5 text-foreground",
    description: "text-sm leading-5 text-foreground-secondary",
    // Quiet rather than filled: a toast is already an interruption, and a solid
    // button inside one competes with the real primary action on the page
    // behind it. `self-end` is what puts it under the close button rather than
    // level with the title.
    actionButton: cx("!h-7 !shrink-0 !self-end !rounded-md !border-0 !px-2.5", "!bg-transparent !text-sm !font-medium !text-primary hover:!bg-hover"),
    cancelButton: cx("!ml-1 !h-7 !shrink-0 !self-end !rounded-md !border-0 !px-2.5", "!bg-transparent !text-sm !font-medium !text-foreground-secondary", "hover:!bg-hover"),
    // Inset to the card's own padding so it lines up with the action below it.
    closeButton: cx("!left-auto !right-4 !top-2 !size-6 !translate-x-0 !translate-y-0", "!rounded-md !border-0 !bg-transparent !text-foreground-secondary", "hover:!bg-hover hover:!text-foreground")
  };
}
const Toaster = t0 => {
  const $ = _c(15);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  const {
    isMobile
  } = useIsMobile();
  const resolvedTheme = useThemeStore(_temp) || "light";
  const closeButton = !isMobile;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["right", "left", "top"];
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      zIndex: 9999
    };
    t3 = cx(switzer.className, "toaster group");
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== closeButton) {
    t4 = toastClassNames(closeButton);
    $[5] = closeButton;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = {
      classNames: t4
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      success: <RiCheckboxCircleFill className="size-4 text-status-success-text" />,
      info: <RiInformationFill className="size-4 text-status-info-text" />,
      warning: <RiErrorWarningFill className="size-4 text-status-warning-text" />,
      error: <RiCloseCircleFill className="size-4 text-status-error-text" />,
      loading: <RiLoader2Fill className="size-4 animate-spin text-status-info-text" />
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== closeButton || $[11] !== props || $[12] !== resolvedTheme || $[13] !== t5) {
    t7 = <Sonner theme={resolvedTheme} position="top-right" swipeDirections={t1} closeButton={closeButton} style={t2} className={t3} toastOptions={t5} icons={t6} {...props} />;
    $[10] = closeButton;
    $[11] = props;
    $[12] = resolvedTheme;
    $[13] = t5;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
export { Toaster };
function _temp(state) {
  return state.resolvedTheme;
}